import React, { useEffect, useState } from 'react';
import { parse } from 'url';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

import StyledFilterSwitch from './filterSwitch.style';
import CalendarFilter from '../calendar/calendarFilter';
import TeaserContainer from '../../organisms/teaser-container/teaserContainer';
import EventFilterTags from '../../atoms/event-filter-tags/eventFilterTags';
import Headline from '../../atoms/headline/headline';
import EventDate from '../event-date/eventDate';
import DateTeaser from '../date-teaser/dateTeaser';
import DateHeader from '../date-header/dateHeader';
import Container from '../../layout/container/container';
import CloseIcon from '../../icons/close.svg';

const getQueryParams = () => {
  if (typeof window !== 'undefined') {
    const queryParams = parse(window.location.search).query;
    if (queryParams) {
      return new URLSearchParams(parse(window.location.search).query);
    }
  }
  return new URLSearchParams();
};

const addQueryParam = (key, value, router) => {
  router.push({ pathname: router.pathname, query: { ...router.query, [key]: value } }, null, {
    shallow: true
  });
};

const removeQueryParam = (key, router) => {
  router.push({ pathname: router.pathname, query: { ...router.query, [key]: undefined } }, null, {
    shallow: true
  });
};

const getDateFromUrl = () => {
  const searchParams = getQueryParams();
  const activeDate = searchParams.get('date');
  if (activeDate) {
    return new Date(parseInt(activeDate, 10));
  }
  return new Date();
};

const filterEventsByDate = (events, date) => {
  return events.filter(element => {
    if (element.startDate !== undefined) {
      const elementDate = new Date(element.startDate.c);
      return (
        elementDate.getDate() === date.getDate() &&
        elementDate.getMonth() === date.getMonth() &&
        elementDate.getFullYear() === date.getFullYear()
      );
    }
    return false;
  });
};

const FilterSwitch = ({ categories, locale, listData }) => {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState('calendar');
  const [filtered, setFiltered] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getDateFromUrl());
  const [activeCategory, setActiveCategory] = useState(null);
  const [eventDates, setEventDates] = useState([]);
  const [formattedDate, setFormattedDate] = useState({});

  const router = useRouter();

  const buildDateArray = () => {
    const dateArray = [];
    for (let i = 0; i < listData.data.length; i += 1) {
      if (listData.data[i].startDate) {
        dateArray.push(new Date(listData.data[i].startDate.c));
      }
    }
    setEventDates(dateArray);
  };

  const formatDate = date => {
    return {
      d: date.getDate().toString(),
      m: `0${(date.getMonth() + 1).toString()}`.slice(-2),
      Y: date.getFullYear().toString(),
      w: date.toLocaleDateString(locale, { weekday: 'long' })
    };
  };

  const handleFilterReset = () => {
    setFiltered(false);
  };

  const handleCatFilterChange = activeCat => {
    if (activeCat === null) {
      removeQueryParam('cat', router);
      setFilteredEvents(listData.data);
      handleFilterReset();
      return;
    }

    addQueryParam('cat', parseInt(activeCat, 10), router);

    const catFilteredEvents = listData.data.filter(element => {
      if (element.categories) {
        return element.categories.find(e => e.id === parseInt(activeCat, 10));
      }
      return false;
    });

    setFilteredEvents(catFilteredEvents);
    setFiltered(true);
  };

  useEffect(() => {
    buildDateArray();
    setFormattedDate(formatDate(selectedDate));
    if (typeof window !== 'undefined') {
      const searchParams = getQueryParams();
      const urlParamTab = searchParams.get('tab');
      const activeCat = searchParams.get('cat');
      const activeDate = searchParams.get('date');

      if (!urlParamTab) {
        setActiveTab('calendar');
      }

      if (activeCat && urlParamTab === 'category') {
        setActiveTab('category');
        setFiltered(true);
        setActiveCategory(activeCat);
      } else if ((!urlParamTab || urlParamTab === 'calendar') && activeDate) {
        const date = new Date(parseInt(activeDate, 10));
        setActiveTab('calendar');
        setSelectedDate(date);
        setFilteredEvents(filterEventsByDate(listData.data, date));
        setFiltered(true);

        document
          .getElementsByClassName('filterswitch__headline')[0]
          .scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  useEffect(() => {
    setFormattedDate(formatDate(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      setFilteredEvents(filterEventsByDate(listData.data, selectedDate));
    } else if (activeTab === 'category') {
      handleCatFilterChange(activeCategory);
    }
  }, [activeTab]);

  const handleCalFilterChange = (activeDate, mode) => {
    const newDate = new Date(activeDate);
    const today = new Date();
    let updatedDate = newDate;

    if (moment(newDate).isBefore(moment(today))) {
      updatedDate = today;
    }
    setSelectedDate(updatedDate);

    if (mode === 'date') {
      addQueryParam('date', updatedDate.getTime(), router);
      const filterEvents = filterEventsByDate(listData.data, updatedDate);
      setFilteredEvents(filterEvents);
      setFiltered(true);
    } else {
      setFiltered(false);
      addQueryParam('date', updatedDate.getTime(), router);
      setFilteredEvents(filterEventsByDate(listData.data, getDateFromUrl()));
    }
  };

  const handleTabSwitch = () => {
    const newActiveTab = activeTab === 'calendar' ? 'category' : 'calendar';
    setActiveTab(newActiveTab);
    addQueryParam('tab', newActiveTab, router);
    handleFilterReset();
  };

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const renderOverViewForMonth = () => {
    let currentDate = null;
    let renderHeader = true;
    const currentMonth = selectedDate.getMonth();

    const list = listData.data.map((event, index) => {
      const eventDate = new Date(event.startDate.c);
      if (!currentDate || !isSameDay(currentDate, eventDate)) {
        currentDate = eventDate;
        renderHeader = true;
      } else {
        renderHeader = false;
      }

      // only current month
      if (eventDate.getMonth() !== currentMonth) {
        return null;
      }

      return (
        <>
          {renderHeader && <DateHeader date={currentDate} />}
          <DateTeaser
            slug={event.slug}
            startDate={event.startDate}
            endDate={event.endDate}
            headline={event.eventHeadline}
            copy={event.eventSubline}
            seats={event.seats}
            bookable={event.bookable}
            uri={event.uri}
            divider={index < filteredEvents.length - 1}
          />
        </>
      );
    });

    return list;
  };

  return (
    <StyledFilterSwitch>
      <Headline className="filterswitch__headline">{t('calendar.headline')}</Headline>
      <div className="filterswitch__inner">
        <button
          type="button"
          onClick={handleTabSwitch}
          className={`filterswitch__button
            ${activeTab === 'calendar' ? 'filterswitch__button--active' : null}`}
        >
          <span className="filterswitch__label">{t('calendar.labelCalendar')}</span>
        </button>
        <span className="filterswitch__line" />
        <button
          type="button"
          onClick={handleTabSwitch}
          className={`filterswitch__button
            ${activeTab === 'category' ? 'filterswitch__button--active' : null}`}
        >
          <span className="filterswitch__label">{t('calendar.labelCategory')}</span>
        </button>
      </div>
      {activeTab === 'calendar' && (
        <Container className="wrapper" fillOnMobile>
          <div className="filterswitch__calwrapper">
            <EventDate
              showWeekday
              isBig
              startDate={formattedDate}
              endDate={formattedDate}
              period={false}
            />
            <CalendarFilter
              initialDate={selectedDate}
              filterChange={handleCalFilterChange}
              activeDates={eventDates}
              locale={locale}
            />
          </div>
        </Container>
      )}
      {activeTab === 'category' && (
        <EventFilterTags
          tags={categories.data}
          activeCategory={activeCategory}
          callback={handleCatFilterChange}
        />
      )}
      {filteredEvents.length > 0 && activeTab === 'calendar' && filtered ? (
        <>
          <Container>
            <div className="result__container">
              <DateHeader date={selectedDate} handleCancelClick={handleFilterReset} cancelable />
              {filteredEvents.map((event, index) => {
                return (
                  <>
                    <DateTeaser
                      slug={event.slug}
                      startDate={event.startDate}
                      endDate={event.endDate}
                      headline={event.eventHeadline}
                      copy={event.eventSubline}
                      seats={event.seats}
                      bookable={event.bookable}
                      uri={event.uri}
                      divider={index < filteredEvents.length - 1}
                    />
                  </>
                );
              })}
            </div>
          </Container>
        </>
      ) : (
        <>
          {!filtered && activeTab === 'calendar' ? (
            <Container>
              <div className="result__container">{renderOverViewForMonth()}</div>
            </Container>
          ) : null}
        </>
      )}
      {filteredEvents.length > 0 && activeTab === 'category' && (
        <TeaserContainer data={filteredEvents} />
      )}
      {filteredEvents.length === 0 && filtered && (
        <Container>
          <div className="filterswitch__noResults">
            {activeTab === 'category' ? (
              <p>{t('calendar.emptyResultCategory')}</p>
            ) : (
              <p>{t('calendar.emptyResultCalendar')}</p>
            )}

            <div
              className="filterswitch__noResults__close"
              role="button"
              onClick={handleFilterReset}
              tabIndex={0}
              onKeyDown={handleFilterReset}
            >
              <CloseIcon width={16} height={16} />
            </div>
          </div>
        </Container>
      )}
    </StyledFilterSwitch>
  );
};

FilterSwitch.propTypes = {
  listData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape())
  }).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export default FilterSwitch;
